import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../Common/Configurations/APIService';
import config from '../Common/Configurations/APIConfig';
import { useAuth } from '../Common/Configurations/AuthContext';
import TemplatePreview from '../template-library/TemplatePreview';
import Header from '../Common/Layout/Header';
import WebsiteTabs from '../Components/AllTabs/WebsiteTabs';

const LiveWebsiteEditor = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const[loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Read-only Master Layout & IDs
  const [layout, setLayout] = useState(null);
  const[templateId, setTemplateId] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  // Editor View State (to navigate inside the preview iframe)
  const [viewState, setViewState] = useState({ name: 'home', params: null });

  // --- EDITABLE STATES ---
  const[styleConfig, setStyleConfig] = useState({});
  const [branding, setBranding] = useState({ customColors: {} });
  const [heroData, setHeroData] = useState({ slides:[] });
  const [aboutData, setAboutData] = useState({});
  const [contactData, setContactData] = useState({});
  const [headerMenu, setHeaderMenu] = useState([]);
  const[footerData, setFooterData] = useState({});
  const [destinations, setDestinations] = useState([]);
  const[seoData, setSeoData] = useState({});

  // 1. Fetch the current company's assigned template and overrides
  useEffect(() => {
    const fetchCompanyWebsite = async () => {
      const companyId = user?.cid; 
      if (!companyId) return;

      try {
        // Fetch using Company ID
        const response = await apiService.getAll(`${config.GETTEMPLATEBYCOMPANYIDFOREDIT}/${companyId}`);
        console.log("Template response:", response);
        const data = response.data || response;

        if (data) {
          // Master Layout & IDs
          setLayout(JSON.parse(data.layoutJson));
          setTemplateId(data.templateId || data.companyId); 

          // Parse Branding Overrides
          const parsedBranding = data.customBrandingJson ? JSON.parse(data.customBrandingJson) : { customColors: {} };
          if (!parsedBranding.customColors) parsedBranding.customColors = {};
          setBranding(parsedBranding);

          // MERGE STYLES: Master Template Styles + Agency's Custom Color Overrides
          const masterStyle = data.styleJson ? JSON.parse(data.styleJson) : {};
          setStyleConfig({
            ...masterStyle,
            primary: parsedBranding.customColors?.primary || masterStyle.primary,
            secondary: parsedBranding.customColors?.secondary || masterStyle.secondary,
            body: { ...masterStyle.body, ...parsedBranding.customColors?.body },
            header: { ...masterStyle.header, ...parsedBranding.customColors?.header },
            footer: { ...masterStyle.footer, ...parsedBranding.customColors?.footer },
            topBar: { ...masterStyle.topBar, ...parsedBranding.customColors?.topBar }
          });

          // Set Content States
          setHeroData(data.heroJson ? JSON.parse(data.heroJson) : { slides:[] });
          setAboutData(data.aboutUsJson ? JSON.parse(data.aboutUsJson) : {});
          setContactData(data.contactUsJson ? JSON.parse(data.contactUsJson) : {});
          setHeaderMenu(data.headerJson ? JSON.parse(data.headerJson) :[]);
          setFooterData(data.footerJson ? JSON.parse(data.footerJson) : {});
          setSeoData(data.seoJson ? JSON.parse(data.seoJson) : {});
          
          if (data.destinationsJson) {
              setDestinations(JSON.parse(data.destinationsJson).destinations ||[]);
          }
        }
      } catch (error) {
        console.error("Failed to load website data", error);
      } finally {
        setLoading(false);
      }
    };

    if (user) fetchCompanyWebsite();
  }, [user]);

  // --- LIVE HANDLERS ---
  const handleNavigate = (viewConfig) => {
    setViewState(viewConfig); // Allows clicking around in the preview!
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const url = URL.createObjectURL(file);
      setBranding(prev => ({ ...prev, logoUrl: url }));
    }
  };

  // 🔴 MAGIC COLOR HANDLER: Updates Preview Instantly & Saves to Branding State
  const handleColorChange = (section, key, value) => {
    if (section === 'global') {
      setStyleConfig(prev => ({ ...prev, [key]: value }));
      setBranding(prev => ({ 
        ...prev, 
        customColors: { ...prev.customColors, [key]: value } 
      }));
    } else {
      setStyleConfig(prev => ({ ...prev, [section]: { ...prev[section], [key]: value } }));
      setBranding(prev => ({ 
        ...prev, 
        customColors: { 
          ...prev.customColors, 
          [section]: { ...(prev.customColors[section] || {}), [key]: value } 
        } 
      }));
    }
  };

  const handleSlideChange = (idx, field, value) => {
    const newSlides = [...heroData.slides];
    newSlides[idx][field] = value;
    setHeroData({ slides: newSlides });
  };

  // --- SAVE TO BACKEND ---
  const handlePublish = async () => {
    setSaving(true);
    try {
      const form = new FormData();
      form.append("CompanyId", user.cid);
      form.append("TemplateId", templateId);
      
      // Colors are saved inside Branding automatically!
      form.append("CustomBrandingJson", JSON.stringify(branding)); 
      form.append("HeroJson", JSON.stringify(heroData));
      form.append("AboutUsJson", JSON.stringify(aboutData));
      form.append("ContactUsJson", JSON.stringify(contactData));
      form.append("HeaderJson", JSON.stringify(headerMenu));
      form.append("FooterJson", JSON.stringify(footerData));
      form.append("SeoJson", JSON.stringify(seoData));
      form.append("DestinationsJson", JSON.stringify({ destinations }));

      if (logoFile) {
        form.append("LogoFile", logoFile);
      }

      await apiService.post(config.ASSIGNTEMPLATE, form);
      alert("Website Published Successfully!");
    } catch (error) {
      console.error("Publish error:", error);
      alert("Error saving website. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary mb-3"></div>
        <h4>Loading Visual Editor...</h4>
      </div>
    );
  }

  return (
      <div id="layout-wrapper">

    {/* Top Header */}
    <Header User={user} />

    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">

          <div className="card border-0 shadow-sm">
            
            {/* Website Tabs */}
            <div className="p-2">
              <WebsiteTabs pageName="WEBSITEEDITOR" />
            </div>

            {/* Editor Body */}
            <div className="card-body p-0">

  <div
    style={{
      display: "flex",
      height: "calc(100vh - 220px)",
      overflow: "hidden",
      backgroundColor: "#f8f9fa"
    }}
  >
      
      
      {/* 🔴 LEFT PANE: CONTROLS */}
      <div style={{ width: '400px', background: '#ffffff', borderRight: '1px solid #dee2e6', display: 'flex', flexDirection: 'column', boxShadow: '2px 0 10px rgba(0,0,0,0.05)', zIndex: 10 }}>
        
        {/* Editor Header */}
        <div className="p-3 border-bottom d-flex justify-content-between align-items-center bg-light">
            <h5 className="m-0 fw-bold text-dark"><i className="bi bi-palette me-2"></i>Theme Editor</h5>
            
        </div>

        {/* Scrollable Accordion Area */}
        <div className="flex-grow-1 overflow-auto p-3 custom-scrollbar">
          
          {/* Navigation Helper */}
          <div className="alert alert-primary py-2 d-flex justify-content-between align-items-center mb-4">
             <span className="fw-bold small">Current View:</span>
             <span className="badge bg-primary text-uppercase">{viewState.name}</span>
          </div>

          <div className="accordion" id="editorAccordion">
            
            {/* --- SECTION 1: THEME COLORS --- */}
            <div className="accordion-item border-0 border-bottom">
              <h2 className="accordion-header">
                <button className="accordion-button fw-bold bg-white text-dark" type="button" data-bs-toggle="collapse" data-bs-target="#colors">
                  <i className="bi bi-droplet-fill text-primary me-2"></i> Colors & Typography
                </button>
              </h2>
              <div id="colors" className="accordion-collapse collapse show" data-bs-parent="#editorAccordion">
                <div className="accordion-body bg-light rounded m-2 p-3">
                  
                  <h6 className="fw-bold text-muted small text-uppercase mb-3">Global Colors</h6>
                  <div className="d-flex align-items-center mb-3">
                    <input type="color" className="form-control form-control-color me-3" value={styleConfig.primary || "#000000"} onChange={e => handleColorChange('global', 'primary', e.target.value)} />
                    <label className="form-label m-0 fw-semibold">Primary Brand Color</label>
                  </div>
                  <div className="d-flex align-items-center mb-4">
                    <input type="color" className="form-control form-control-color me-3" value={styleConfig.secondary || "#000000"} onChange={e => handleColorChange('global', 'secondary', e.target.value)} />
                    <label className="form-label m-0 fw-semibold">Secondary Accent</label>
                  </div>

                  <h6 className="fw-bold text-muted small text-uppercase mb-3">Section Colors</h6>
                  <div className="row g-2 mb-3">
                     <div className="col-6">
                        <label className="form-label small">Header BG</label>
                        <input type="color" className="form-control form-control-color w-100" value={styleConfig.header?.bgColor || "#ffffff"} onChange={e => handleColorChange('header', 'bgColor', e.target.value)} />
                     </div>
                     <div className="col-6">
                        <label className="form-label small">Header Text</label>
                        <input type="color" className="form-control form-control-color w-100" value={styleConfig.header?.textColor || "#000000"} onChange={e => handleColorChange('header', 'textColor', e.target.value)} />
                     </div>
                  </div>

                  <div className="row g-2">
                     <div className="col-6">
                        <label className="form-label small">Footer BG</label>
                        <input type="color" className="form-control form-control-color w-100" value={styleConfig.footer?.bgColor || "#000000"} onChange={e => handleColorChange('footer', 'bgColor', e.target.value)} />
                     </div>
                     <div className="col-6">
                        <label className="form-label small">Footer Text</label>
                        <input type="color" className="form-control form-control-color w-100" value={styleConfig.footer?.textColor || "#ffffff"} onChange={e => handleColorChange('footer', 'textColor', e.target.value)} />
                     </div>
                  </div>

                </div>
              </div>
            </div>

            {/* --- SECTION 2: BRANDING & LOGO --- */}
            <div className="accordion-item border-0 border-bottom">
              <h2 className="accordion-header">
                <button className="accordion-button collapsed fw-bold bg-white text-dark" type="button" data-bs-toggle="collapse" data-bs-target="#branding">
                  <i className="bi bi-image text-primary me-2"></i> Logo & Branding
                </button>
              </h2>
              <div id="branding" className="accordion-collapse collapse" data-bs-parent="#editorAccordion">
                <div className="accordion-body p-3">
                  <label className="form-label fw-bold small">Upload Logo</label>
                  {branding.logoUrl && <img src={branding.logoUrl} alt="Logo" className="img-fluid rounded border mb-2 bg-light p-2" style={{ maxHeight: '80px', objectFit: 'contain', width:'100%' }} />}
                  <input type="file" className="form-control form-control-sm mb-3" accept="image/*" onChange={handleLogoUpload} />

                  <label className="form-label fw-bold small">Brand Name</label>
                  <input type="text" className="form-control mb-3" value={branding.brandName || ''} onChange={e => setBranding({...branding, brandName: e.target.value})} />

                  <label className="form-label fw-bold small">Contact Email</label>
                  <input type="email" className="form-control mb-3" value={branding.email || ''} onChange={e => setBranding({...branding, email: e.target.value})} />
                </div>
              </div>
            </div>

            {/* --- SECTION 3: HERO BANNER --- */}
            <div className="accordion-item border-0 border-bottom">
              <h2 className="accordion-header">
                <button className="accordion-button collapsed fw-bold bg-white text-dark" type="button" data-bs-toggle="collapse" data-bs-target="#hero">
                  <i className="bi bi-card-image text-primary me-2"></i> Hero Banner
                </button>
              </h2>
              <div id="hero" className="accordion-collapse collapse" data-bs-parent="#editorAccordion">
                <div className="accordion-body p-3">
                   {heroData.slides.map((slide, idx) => (
                       <div key={idx} className="mb-4 p-3 border rounded shadow-sm bg-light">
                           <h6 className="fw-bold text-primary mb-3">Slide {idx + 1}</h6>
                           <label className="form-label small fw-bold">Title</label>
                           <input type="text" className="form-control form-control-sm mb-2" value={slide.title} onChange={(e) => handleSlideChange(idx, 'title', e.target.value)} />
                           
                           <label className="form-label small fw-bold">Subtitle</label>
                           <input type="text" className="form-control form-control-sm mb-2" value={slide.subtitle || (slide.subtitles && slide.subtitles[0]) || ''} onChange={(e) => handleSlideChange(idx, 'subtitle', e.target.value)} />

                           <label className="form-label small fw-bold">Image URL</label>
                           <input type="text" className="form-control form-control-sm" value={slide.image} onChange={(e) => handleSlideChange(idx, 'image', e.target.value)} />
                       </div>
                   ))}
                </div>
              </div>
            </div>

            {/* --- SECTION 4: ABOUT US --- */}
            <div className="accordion-item border-0 border-bottom">
              <h2 className="accordion-header">
                <button className="accordion-button collapsed fw-bold bg-white text-dark" type="button" data-bs-toggle="collapse" data-bs-target="#about">
                  <i className="bi bi-info-circle text-primary me-2"></i> About Us Section
                </button>
              </h2>
              <div id="about" className="accordion-collapse collapse" data-bs-parent="#editorAccordion">
                <div className="accordion-body p-3">
                  <label className="form-label small fw-bold">Section Title</label>
                  <input type="text" className="form-control mb-3" value={aboutData.title || ''} onChange={e => setAboutData({...aboutData, title: e.target.value})} />
                  
                  <label className="form-label small fw-bold">Description Text</label>
                  <textarea className="form-control mb-3" rows="5" value={aboutData.description || ''} onChange={e => setAboutData({...aboutData, description: e.target.value})} />

                  <label className="form-label small fw-bold">Image URL</label>
                  <input type="text" className="form-control mb-2" value={aboutData.image || ''} onChange={e => setAboutData({...aboutData, image: e.target.value})} />
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Save Button Footer */}
        <div className="p-3 bg-white border-top shadow-lg">
            <button className="btn btn-success w-100 fw-bold py-2" onClick={handlePublish} disabled={saving}>
                {saving ? (
                  <><span className="spinner-border spinner-border-sm me-2"></span> Publishing...</>
                ) : (
                  <><i className="bi bi-cloud-arrow-up-fill me-2"></i> Publish Changes</>
                )}
            </button>
            <p className="text-center text-muted small mt-2 mb-0">Changes will be live instantly.</p>
        </div>
      </div>

      {/* 🔴 RIGHT PANE: LIVE PREVIEW IFRAME SIMULATOR */}
      <div style={{ flex: 1, backgroundColor: '#e9ecef', padding: '20px', overflowY: 'auto', position: 'relative' }}>
        
        {/* Browser Mockup Wrapper */}
        <div className="shadow-lg rounded overflow-hidden d-flex flex-column mx-auto" style={{ height: '100%', maxWidth: '1400px', backgroundColor: '#fff', border: '1px solid #ccc' }}>
            
         
    

            {/* The Actual Preview Content */}
            {/* Note: mode="preview" is used to ensure links map correctly to handleNavigate */}
            <div className="flex-grow-1 overflow-auto position-relative custom-scrollbar">
                <TemplatePreview 
                    layout={layout} 
                    styleConfig={styleConfig} 
                    branding={branding}
                    destinations={destinations}
                    heroData={heroData}
                    headerMenu={headerMenu}
                    footerData={footerData}
                    aboutData={aboutData}
                    contactData={contactData}
                    viewState={viewState} 
                    onNavigate={handleNavigate} 
                    mode="builder" 
                />
            </div>
        </div>

      </div>
    </div>
    </div>
    </div>
    </div>
    </div>
    </div>
    </div>
  );
};

export default LiveWebsiteEditor;